import * as React from "react";

import { StatusPill } from "@/components/ui/status-pill";
import { cn } from "@/lib/utils";

export type EditResolution = "pending" | "accepted" | "rejected";

export interface EditIdentityProps {
  deletedText?: string | null;
  insertedText?: string | null;
  reason?: string | null;
  status?: EditResolution;
  /** Scale: `row` is compact single-line, `card` is full diff view. */
  scale?: "row" | "card";
  className?: string;
}

const statusVariant: Record<EditResolution, "neutral" | "success" | "destructive"> = {
  pending: "neutral",
  accepted: "success",
  rejected: "destructive",
};

const EditIdentity = React.forwardRef<HTMLDivElement, EditIdentityProps>(
  ({ deletedText, insertedText, reason, status = "pending", scale = "card", className }, ref) => {
    const variant = statusVariant[status];

    if (scale === "row") {
      return (
        <div ref={ref} className={cn("inline-flex min-w-0 items-center gap-2", className)}>
          <span className="min-w-0 truncate text-sm">
            {deletedText ? (
              <span data-kind="deletion" className="text-deletion line-through">
                {deletedText}
              </span>
            ) : null}
            {deletedText && insertedText ? " " : null}
            {insertedText ? (
              <span data-kind="insertion" className="text-insertion underline underline-offset-2">
                {insertedText}
              </span>
            ) : null}
            {!deletedText && !insertedText ? <span className="text-muted-foreground">No change text.</span> : null}
          </span>
          <StatusPill variant={variant} className="shrink-0">
            {status}
          </StatusPill>
        </div>
      );
    }

    // card scale
    return (
      <div ref={ref} className={cn("space-y-2", className)}>
        <div className="flex items-start justify-between gap-2">
          {reason ? <p className="min-w-0 text-sm">{reason}</p> : <span />}
          <StatusPill variant={variant} className="shrink-0">
            {status}
          </StatusPill>
        </div>
        <div className="rounded-md bg-muted/40 p-3 text-sm leading-6">
          {deletedText ? (
            <p data-kind="deletion" className="text-deletion line-through decoration-deletion">
              {deletedText}
            </p>
          ) : null}
          {insertedText ? (
            <p data-kind="insertion" className="text-insertion underline decoration-insertion underline-offset-2">
              {insertedText}
            </p>
          ) : null}
          {!deletedText && !insertedText ? (
            <p className="text-muted-foreground">No change text.</p>
          ) : null}
        </div>
      </div>
    );
  }
);
EditIdentity.displayName = "EditIdentity";

export { EditIdentity };
